import { z } from 'zod'

/**
 * School material extraction boundary (Slice 3, D-14.3), the client side of
 * the `extract-material` Edge Function (`supabase/functions/extract-material/
 * contract.ts`; keep the two in step).
 *
 * Every response is validated here: an extraction is never trusted because it
 * arrived, and a reading the schema cannot place is an `invalid_response`
 * failure, not a partial success. Extraction is not understanding: a block's
 * text is what the provider read, and `status` says how sure that reading is.
 */

const regionSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  width: z.number().gt(0).max(1),
  height: z.number().gt(0).max(1),
})

const blockSchema = z
  .object({
    id: z.string().min(1),
    page: z.number().int().min(1),
    order: z.number().int().min(0),
    kind: z.enum(['question', 'instruction', 'formula', 'text']),
    text: z.string(),
    status: z.enum(['clear', 'uncertain', 'unreadable']),
    reason: z.enum(['low_confidence', 'no_confidence', 'unreadable']).nullable(),
    confidence: z.number().min(0).max(1).nullable(),
    region: regionSchema.nullable(),
    uncertainSpans: z.array(
      z.object({ start: z.number().int().min(0), end: z.number().int().min(0) }),
    ),
  })
  // A clear block has no reason; a doubtful one always says why.
  .refine((b) => (b.status === 'clear') === (b.reason === null))
  .refine((b) => b.uncertainSpans.every((s) => s.start < s.end && s.end <= b.text.length))

export const extractionSchema = z
  .object({
    id: z.string().min(1),
    subject: z.enum(['mathematics', 'english']),
    documentSha256: z.string().regex(/^[0-9a-f]{64}$/),
    mimeType: z.string().min(1),
    bytes: z.number().int().positive(),
    pageCount: z.number().int().min(1),
    provider: z.object({ id: z.string().min(1), name: z.string().min(1) }),
    extractedAt: z.iso.datetime(),
    /** The server's conservative check; `uncertain` asks the learner to look closely. */
    classification: z.enum(['learning', 'uncertain']),
    blocks: z.array(blockSchema).min(1),
  })
  .refine((e) => e.blocks.every((b) => b.page <= e.pageCount))

export type Extraction = z.infer<typeof extractionSchema>
export type MaterialSubject = Extraction['subject']
export type ExtractedBlock = Extraction['blocks'][number]
export type BlockStatus = ExtractedBlock['status']
export type BlockKind = ExtractedBlock['kind']
export type UncertainReason = NonNullable<ExtractedBlock['reason']>
export type Region = z.infer<typeof regionSchema>

export const SERVER_ERROR_CODES = [
  'bad_request',
  'not_authenticated',
  'learner_session_invalid',
  'not_entitled',
  'consent_required',
  'subject_unavailable',
  'restricted',
  'in_progress',
  'not_learning_material',
  'unsupported_type',
  'empty',
  'too_large',
  'unreadable_document',
  'no_text_found',
  'provider_not_configured',
  'provider_failed',
  'provider_timeout',
] as const

export const extractResponseSchema = z.discriminatedUnion('ok', [
  z.object({ ok: z.literal(true), extraction: extractionSchema }),
  z.object({
    ok: z.literal(false),
    error: z.enum(SERVER_ERROR_CODES),
    restrictedUntil: z.iso.datetime().nullable().optional(),
  }),
])

const uploadStateSchema = z.object({
  entitled: z.boolean(),
  consent: z.boolean(),
  restricted: z.boolean(),
  restrictedUntil: z.iso.datetime({ offset: true }).nullable(),
  learnerSession: z.boolean(),
})

export const availabilitySchema = z.discriminatedUnion('available', [
  z.object({ available: z.literal(false), upload: uploadStateSchema }),
  z.object({
    available: z.literal(true),
    provider: z.object({ id: z.string().min(1), name: z.string().min(1) }),
    notice: z.string().min(1),
    maxBytes: z.number().int().positive(),
    subjects: z.array(z.string()),
    syntheticFixtureSubjects: z.array(z.string()).optional(),
    acceptedTypes: z.array(z.string()),
    upload: uploadStateSchema,
  }),
])

/**
 * Why reading is not offered, most important first:
 *   `not_entitled`      the family's upload entitlement is off (D-15);
 *   `learner_session_invalid`  no live learner session (D-16): the learner
 *                       must enter their PIN again;
 *   `restricted`        too many non-learning uploads in seven days;
 *   `consent_required`  no parent consent to external processing (NFR-05);
 *   `not_configured`    no extraction provider configured;
 *   `unreachable`       the request never got an answer (offline, blocked);
 *   `not_authenticated` the parent's sign-in was refused (HTTP 401);
 *   `provider_failed`   the function or its gate failed on the server (5xx);
 *   `invalid_response`  an answer that is not the documented shape;
 *   `disabled`          not in this build.
 */
export type UnavailableReason =
  | 'not_entitled'
  | 'learner_session_invalid'
  | 'restricted'
  | 'consent_required'
  | 'not_configured'
  | 'unreachable'
  | 'not_authenticated'
  | 'provider_failed'
  | 'invalid_response'
  | 'disabled'

export type Availability =
  | {
      readonly available: false
      readonly why: UnavailableReason
      /** With `restricted`: when uploading opens again. */
      readonly restrictedUntil?: string | null
    }
  | {
      readonly available: true
      readonly provider: { readonly id: string; readonly name: string }
      readonly notice: string
      readonly maxBytes: number
      /** Subjects the server lets the learner send now; absent means Mathematics only. */
      readonly subjects?: readonly MaterialSubject[]
      /** Local fixture stack only: subjects offered solely as the synthetic TEST FIXTURE. */
      readonly syntheticFixtureSubjects?: readonly MaterialSubject[]
    }

/** Why reading failed, in terms the UI can explain and act on. */
export type ExtractionFailure =
  (typeof SERVER_ERROR_CODES)[number] | 'network' | 'cancelled' | 'invalid_response'

export class ExtractionError extends Error {
  readonly code: ExtractionFailure
  /** With `restricted` / `not_learning_material`: when uploading opens again, if restricted. */
  readonly restrictedUntil: string | null

  constructor(code: ExtractionFailure, restrictedUntil: string | null = null) {
    super(`material extraction failed: ${code}`)
    this.name = 'ExtractionError'
    this.code = code
    this.restrictedUntil = restrictedUntil
  }
}

export interface ExtractRequest {
  readonly file: File
  readonly subject: 'mathematics' | 'english'
  /**
   * One per chosen file; reused by "Thử lại" so the server never counts the
   * same upload twice.
   */
  readonly requestId: string
  /**
   * Ask explicitly for the synthetic TEST FIXTURE (local stack only), for a
   * subject the server offers only that way. The server decides; this is no
   * permission by itself.
   */
  readonly syntheticFixture?: boolean
  /** Aborting cancels the request; the promise rejects with `cancelled`. */
  readonly signal: AbortSignal
}

/**
 * Reads one school document for the learner of the current learner session.
 * Bound to the session by the app shell, so screens never handle the token;
 * the server derives the learner from the session, never from the request.
 */
export interface MaterialReader {
  availability(): Promise<Availability>
  extract(request: ExtractRequest): Promise<Extraction>
}

export interface MaterialExtractionService {
  availability(sessionToken: string): Promise<Availability>
  extract(sessionToken: string, request: ExtractRequest): Promise<Extraction>
}

export function bindReader(
  service: MaterialExtractionService,
  sessionToken: string,
): MaterialReader {
  return {
    availability: () => service.availability(sessionToken),
    extract: (request) => service.extract(sessionToken, request),
  }
}

/** For builds where reading may not run at all (e.g. pilot, or a hosted URL in development). */
export function createDisabledExtractionService(): MaterialExtractionService {
  return {
    availability: () => Promise.resolve({ available: false, why: 'disabled' }),
    extract: () => Promise.reject(new ExtractionError('provider_not_configured')),
  }
}
